import React, { useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    InputLabel,
    OutlinedInput,
    Select,
    TextField,
} from "@mui/material";

import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { formatDate } from "../../../../utilities/formatters";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";

const areas = [
    { id: 1, name: "Academic" },
    { id: 2, name: "Clinical" },
    { id: 3, name: "Management" },
    { id: 4, name: "Other Roles (inc voluntary)" },
];

const contractTypes = [
    { id: 1, name: "Contracted" },
    { id: 2, name: "Employed" },
    { id: 3, name: "Honorary" },
    { id: 4, name: "Other (please state)" },
    { id: 5, name: "Practising Privileges" },
    { id: 6, name: "Voluntary" },
];

function ScopeOfWorkFormDialog(props) {
    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            fullWidth={true}
            maxWidth="md"
        >
            <DialogTitle>{props.formTitle}</DialogTitle>
            <DialogContent dividers={false}>
                <Box
                    component="form"
                    autoComplete="off"
                    noValidate
                    className="dialog-content"
                    onSubmit={(e) => e.preventDefault()}
                >
                    <FormControl
                        component="div"
                        margin={"normal"}
                        fullWidth={true}
                        required={true}
                        error={!props?.description}
                    >
                        <InputLabel
                            id="label-description"
                            sx={{ backgroundColor: "#FFF", padding: "0 4px" }}
                        >
                            Description
                        </InputLabel>
                        <OutlinedInput
                            id="text-description"
                            value={props.description}
                            autoFocus={true}
                            inputProps={{
                                maxLength: 250,
                            }}
                            onChange={({ target }) =>
                                props.setDescription(target.value)
                            }
                        />
                        <FormHelperText>
                            Briefly describe the scope of work
                        </FormHelperText>
                    </FormControl>

                    <FormControl
                        component="div"
                        margin={"normal"}
                        fullWidth={true}
                        required={true}
                        error={!props?.organisation}
                    >
                        <InputLabel
                            id="label-organisation"
                            sx={{ backgroundColor: "#FFF", padding: "0 4px" }}
                        >
                            Organisation
                        </InputLabel>
                        <OutlinedInput
                            id="text-organisation"
                            value={props.organisation}
                            onChange={({ target }) =>
                                props.setOrganisation(target.value)
                            }
                        />
                        <FormHelperText>Name of organisation</FormHelperText>
                    </FormControl>

                    <FormControl
                        component="div"
                        margin={"normal"}
                        fullWidth={true}
                        required={true}
                        error={!props?.area}
                    >
                        <InputLabel
                            id="label-area"
                            sx={{ backgroundColor: "#FFF", padding: "0 4px" }}
                        >
                            Area of work
                        </InputLabel>
                        <Select
                            id="select-area"
                            value={props.area}
                            onChange={({ target }) =>
                                props.setArea(target.value)
                            }
                        >
                            {areas?.length > 0 &&
                                areas.map((option) => (
                                    <MenuItem
                                        value={option.name}
                                        key={option.id}
                                    >
                                        {option.name}
                                    </MenuItem>
                                ))}
                        </Select>
                        <FormHelperText>Select area of work</FormHelperText>
                    </FormControl>

                    <FormControl
                        component="div"
                        margin={"normal"}
                        fullWidth={true}
                        required={true}
                        error={!props?.contract}
                    >
                        <InputLabel
                            id="label-area"
                            sx={{ backgroundColor: "#FFF", padding: "0 4px" }}
                        >
                            Contract type
                        </InputLabel>
                        <Select
                            id="select-contract"
                            value={props.contract}
                            onChange={({ target }) =>
                                props.setContract(target.value)
                            }
                        >
                            {contractTypes?.length > 0 &&
                                contractTypes.map((option) => (
                                    <MenuItem
                                        value={option.name}
                                        key={option.id}
                                    >
                                        {option.name}
                                    </MenuItem>
                                ))}
                        </Select>
                        <FormHelperText>Select contract type</FormHelperText>
                    </FormControl>

                    <Box className="flex-container">
                        <FormControl
                            component="div"
                            margin={"normal"}
                            required={true}
                            error={!props?.dateStart}
                        >
                            <DatePicker
                                label="Start *"
                                value={dayjs(props.dateStart || null)}
                                format="DD/MM/YYYY"
                                onChange={props.setDateStart}
                                renderInput={(params) => (
                                    <TextField {...params} />
                                )}
                                maxDate={dayjs(props.dateEnd || null)}
                                required={true}
                            />
                            <FormHelperText>
                                Select a start date.
                            </FormHelperText>
                        </FormControl>

                        <FormControl component="div" margin={"normal"}>
                            <DatePicker
                                label="End"
                                value={dayjs(props.dateEnd || null)}
                                format="DD/MM/YYYY"
                                onChange={props.setDateEnd}
                                renderInput={(params) => (
                                    <TextField {...params} />
                                )}
                                minDate={dayjs(props.dateStart || null)}
                            />

                            <FormHelperText>Select a end date.</FormHelperText>
                        </FormControl>
                    </Box>

                    <Box className="flex-container">
                        <FormControl
                            component="div"
                            margin={"normal"}
                            sx={{ width: "25ch" }}
                            required={!props.isAdHoc}
                            error={!props.isAdHoc && !props.timeCommitted}
                        >
                            <InputLabel
                                id="label-timeCommitted"
                                sx={{
                                    backgroundColor: "#FFF",
                                    padding: "0 4px",
                                }}
                            >
                                Time
                            </InputLabel>
                            <OutlinedInput
                                id="text-timeCommitted"
                                value={props.timeCommitted}
                                type={"number"}
                                inputProps={{
                                    min: 0,
                                    max: 100,
                                    step: 1,
                                    inputMode: "numeric", // Only allow numeric input
                                }}
                                onChange={({ target }) =>
                                    target.value >= 0 &&
                                    target.value <= 100 &&
                                    props.setTimeCommitted(target.value)
                                }
                            />
                            <FormHelperText>total commitment</FormHelperText>
                        </FormControl>

                        <FormGroup>
                            <FormControlLabel
                                label="Ad hoc"
                                control={
                                    <Checkbox
                                        value={!!props.isAdHoc}
                                        checked={!!props.isAdHoc}
                                        onChange={(e, checked) =>
                                            props.setAdHoc(checked)
                                        }
                                    />
                                }
                            />
                        </FormGroup>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button key="cancel" onClick={props.onClose}>
                    Cancel
                </Button>
                <Button
                    key="save"
                    onClick={props.onSubmit}
                    variant="contained"
                    disableElevation={true}
                    color="primary"
                    disabled={
                        !props.area ||
                        !props.description ||
                        !props.organisation ||
                        !props.contract ||
                        !props.dateStart ||
                        (!props.isAdHoc && !props.timeCommitted)
                    }
                >
                    {props.submitLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function ScopeOfWorkFormDialogContainer({
    formTitle,
    submitLabel = "Save",
    onSave,
    onClose,
    data = {},
    ...props
}) {
    const [description, setDescription] = useState(data.description || "");
    const [area, setArea] = useState(data.area || "");
    const [organisation, setOrganisation] = useState(
        data.organisation?.name || ""
    );
    const [dateStart, setDateStart] = useState(data.dateStart || "");
    const [dateEnd, setDateEnd] = useState(data.dateEnd || "");
    const [contract, setContract] = useState(data.contract || "");
    const [isAdHoc, setAdHoc] = useState(data.work === "Ad hoc");
    const [timeCommitted, setTimeCommitted] = useState(
        data.practicePercent || ""
    );

    const onSubmit = () => {
        const params = {
            description,
            area,
            organisation: { name: organisation },
            dateStart: formatDate(dateStart),
            dateEnd: formatDate(dateEnd),
            contract,
            work: isAdHoc ? "Ad hoc" : "Regular",
            practicePercent: timeCommitted,
        };
        onSave(params);
        onClose();
    };

    return (
        <ScopeOfWorkFormDialog
            formTitle={formTitle}
            description={description}
            setDescription={setDescription}
            area={area}
            setArea={setArea}
            organisation={organisation}
            setOrganisation={setOrganisation}
            dateStart={dateStart}
            setDateStart={setDateStart}
            dateEnd={dateEnd}
            setDateEnd={setDateEnd}
            contract={contract}
            setContract={setContract}
            isAdHoc={isAdHoc}
            setAdHoc={setAdHoc}
            timeCommitted={timeCommitted}
            setTimeCommitted={setTimeCommitted}
            onClose={onClose}
            onSubmit={onSubmit}
            submitLabel={submitLabel}
            {...props}
        />
    );
}

export default ScopeOfWorkFormDialogContainer;
